import { Component, OnInit } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { CapitanLocation, StripeReader } from '../../../../../../../models/location';
import { AddStripeReaderModalComponent } from './modals/add-stripe-reader-modal/add-stripe-reader-modal.component';
import { CallbackResult, CallbackResultStatus } from '../../../../../../../utils';
import { EditStripeReaderModalComponent } from './modals/edit-stripe-reader-modal/edit-stripe-reader-modal.component';
import { RemoveStripeReaderModalComponent } from './modals/remove-stripe-reader-modal/remove-stripe-reader-modal.component';
import { LocationService } from '../../../../../../../services/location/location.service';
import { AccountService } from '../../../../../../../services/account/account.service';
import { PaymentProcessorConnection } from '../../../../../../../models/organization-config';
import { OrganizationConfigService } from '../../../../../../../services/organization-config/organization-config.service';
import { ActivatedRoute } from '@angular/router';
import { PaymentProcessorType } from '../../../../../../../models/organization-config-enums';
import { AddMxMerchantTerminalModalComponent } from './modals/add-mx-merchant-terminal-modal/add-mx-merchant-terminal-modal.component';


@Component({
  selector: 'app-stripe-readers-config',
  templateUrl: './stripe-readers-config.component.html',
  styleUrls: ['./stripe-readers-config.component.css']
})
export class StripeReadersConfigComponent implements OnInit {
  public connId: number;

  public selectedLocation: CapitanLocation = null;

  public conn: PaymentProcessorConnection;
  public readerList: StripeReader[];
  public locations: CapitanLocation[];

  public loadingConn = true;
  public loadingReaders = true;
  public loadingLocations = true;
  public loadingSelectLocation = false;

  public connNotFound = false;
  public serverError: number = null;
  public errorMessage = '';

  // Make browser history available to the template.
  public history: History;

  public modalResult: CallbackResult = null;

  // Create instance references to lookup functions and enums so they may be
  // called in the template.
  public CallbackResultStatus = CallbackResultStatus;

  constructor(
    public locationService: LocationService,
    private route: ActivatedRoute,
    private accountService: AccountService,
    private orgConfigService: OrganizationConfigService,
    private modalService: NgbModal
  ) {}

  ngOnInit() {
    if (!this.accountService.getMyAccount().is_organization_admin) {
      this.errorMessage = 'You do not have permission to configure payment terminals.';
      return;
    }

    // Make browser history available to the template.
    this.history = history;

    const routeParams = this.route.snapshot.params;

    this.connId = parseInt(routeParams.id, 10);
    if (isNaN(this.connId)) {
      this.connNotFound = true;
      return;
    }

    this.loadConn();
    this.loadReaders();
    this.loadLocations();
  }

  public readerResultsPresent() {
    const readersForLocation = this.getReadersForLocation();

    return readersForLocation && (readersForLocation.length > 0);
  }

  public getReadersForLocation() {
    if (!this.readerList) { return null; }
    if (!this.selectedLocation) { return null;}

    return this.readerList.filter(
      (reader) => {
        return (reader.location_id === this.selectedLocation.id);
      }
    )
  }

  public openAddTerminalModal() {
    this.modalResult = null;

    if (this.conn.payment_processor === PaymentProcessorType.stripe) {
      const modalRef = this.modalService.open(AddStripeReaderModalComponent);
      modalRef.componentInstance.location = this.selectedLocation;
      modalRef.componentInstance.savedCallback = (result: CallbackResult) => {
        this.modalResult = result;
        this.loadReaders();
      };
    } else if (this.conn.payment_processor === PaymentProcessorType.mxMerchant) {
      const modalRef = this.modalService.open(AddMxMerchantTerminalModalComponent);
      modalRef.componentInstance.location = this.selectedLocation;
      modalRef.componentInstance.conn = this.conn;
      modalRef.componentInstance.savedCallback = (result: CallbackResult) => {
        this.modalResult = result;
        this.loadReaders();
      };
    } else {
      return;
    }
  }

  public openEditReaderModal(reader: StripeReader) {
    // Create a copy of the reader so that the reader's info is only
    // updated in the table after it is saved to the API.
    const readerInput: StripeReader = {...reader};

    this.modalResult = null;

    const modalRef = this.modalService.open(EditStripeReaderModalComponent);
    modalRef.componentInstance.reader = readerInput;
    modalRef.componentInstance.savedCallback = (result: CallbackResult) => {
      this.modalResult = result;
      this.loadReaders();
    };
  }

  public openRemoveReaderModal(reader: StripeReader) {
    this.modalResult = null;

    const modalRef = this.modalService.open(RemoveStripeReaderModalComponent);
    modalRef.componentInstance.reader = reader;
    modalRef.componentInstance.savedCallback = (result: CallbackResult) => {
      this.modalResult = result;
      this.loadReaders();
    };
  }

  public loadingInit() {
    return this.loadingConn || this.loadingReaders || this.loadingLocations;
  }

  private loadConn() {
    this.loadingConn = true;

    this.orgConfigService
      .getPaymentProcessorConnection(this.connId)
      .then(result => {
        this.conn = result;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 404) {
          this.connNotFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => {
        this.loadingConn = false;
      });
  }

  private loadReaders() {
    this.loadingReaders = true;

    this.locationService
      .getTerminals()
      .then(response => { this.readerList = response; })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingReaders = false; });
  }

  private loadLocations() {
    this.loadingLocations = true;

    this.locationService
      .loadLocations()
      .then(() => {
        this.locations = this.locationService
          .getLocations()
          .filter(
            (loc) => loc.payment_processor_connection_id === this.connId
          );

        for (const location of this.locations) {
          if (location.id === this.accountService.getDefaultLocation().id) {
            this.selectedLocation = location;
          }
        }
        if ((this.selectedLocation === null) && (this.locations.length > 0)) {
          this.selectedLocation = this.locations[0];
        }
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingLocations = false; });
  }

}
